import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";

const DEFAULT_WIDTH = 100;
const MIN_WIDTH = 50;

function ResizableDataGrid({ columns: columnsProp, ...rest }) {
  const apiRef = useRef(null);
  const resizeStateRef = useRef({ active: false, field: null, startX: 0, startWidth: 0 });

  const [columnWidths, setColumnWidths] = useState(() => {
    const initial = {};
    columnsProp.forEach((col) => {
      if (col.field != null && (col.width != null || col.flex == null)) {
        initial[col.field] = col.width ?? DEFAULT_WIDTH;
      }
    });
    return initial;
  });

  const columns = useMemo(() => {
    return columnsProp.map((col) => {
      if (col.field == null || col.flex != null) return { ...col };
      const width = columnWidths[col.field] ?? col.width ?? DEFAULT_WIDTH;
      return { ...col, width: Number(width) || DEFAULT_WIDTH, resizable: true };
    });
  }, [columnsProp, columnWidths]);

  useEffect(() => {
    let unsub;
    const id = setTimeout(() => {
      const api = apiRef.current;
      if (!api?.subscribeEvent) return;
      unsub = api.subscribeEvent("columnSeparatorMouseDown", (params, event) => {
        const field = params?.field;
        if (!field) return;
        const col = api.getColumn(field);
        const startWidth = col?.computedWidth ?? col?.width ?? DEFAULT_WIDTH;
        const minW = col?.minWidth ?? MIN_WIDTH;
        const maxW = col?.maxWidth ?? 9999;
        resizeStateRef.current = { active: true, field, startX: event.clientX, startWidth, minW, maxW };
        const onMouseMove = (e) => {
          if (!resizeStateRef.current.active) return;
          const { field: f, startX, startWidth: sw, minW: mn, maxW: mx } = resizeStateRef.current;
          const delta = e.clientX - startX;
          const newWidth = Math.max(mn, Math.min(mx, sw + delta));
          api.setColumnWidth(f, Math.round(newWidth));
          setColumnWidths((prev) => ({ ...prev, [f]: Math.round(newWidth) }));
        };
        const onMouseUp = () => {
          resizeStateRef.current.active = false;
          window.removeEventListener("mousemove", onMouseMove);
          window.removeEventListener("mouseup", onMouseUp);
        };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
      });
    }, 100);
    return () => {
      clearTimeout(id);
      if (typeof unsub === "function") unsub();
    };
  }, []);

  return (
    <DataGrid
      apiRef={apiRef}
      columns={columns}
      disableColumnResize={false}
      showColumnVerticalBorder={false}
      sx={{
        // White line so you know where the column resize slider is (drag here to resize)
        "& .MuiDataGrid-columnSeparator": {
          visibility: "visible !important",
          opacity: "1 !important",
          color: "#fff",
          width: 14,
          minWidth: 14,
          position: "relative",
          cursor: "col-resize",
          "&::after": {
            content: '""',
            position: "absolute",
            left: "50%",
            top: "15%",
            bottom: "15%",
            width: 3,
            marginLeft: -1.5,
            backgroundColor: "#fff",
            borderRadius: 1,
            pointerEvents: "none",
          },
          "&:hover": {
            color: "#fff",
            cursor: "col-resize",
            "&::after": { backgroundColor: "#fff", opacity: 1 },
          },
          "& .MuiSvgIcon-root": {
            fontSize: 18,
            color: "#fff",
          },
        },
        "& .MuiDataGrid-columnSeparator--resizable": {
          cursor: "col-resize",
          "&:hover": { cursor: "col-resize" },
        },
      }}
      {...rest}
    />
  );
}

export default ResizableDataGrid;
